import { Injectable, signal } from '@angular/core';

/**
 * What the card is about to be used for.
 * - `payment`   — a one-off / initial charge.
 * - `recurring` — anything charged again on a schedule (plan instalments,
 *                 subscriptions) and backup cards, which take those charges
 *                 when the primary fails.
 */
export type PrepaidCardUsage = 'payment' | 'recurring';

export const PREPAID_CARD_MESSAGES = {
  notAccepted: "We don't accept prepaid cards. Please use a debit or credit card to continue with your payment.",
  notAcceptedForRecurring: 'Prepaid cards are not accepted for recurring payments. Please use a debit or credit card to continue with your payment.',
  // Inline variant shown under a recurring payment method section.
  recurringInline: 'Prepaid card not accepted — This payment plan cannot be set up with a prepaid card. Please use another debit or credit card, or contact support team.'
} as const;

const PREPAID_SETTINGS_KEY = 'prepaidCardSettings';

/**
 * Single source of truth for the prepaid card rules from
 * `doctor_settings[0]` (access_token_login, cached by AuthService as
 * localStorage.prepaidCardSettings; falls back to localStorage.doctorSettings):
 *
 * | allow_prepaid_card | prepaid_for_recurring | payment | recurring / backup |
 * |--------------------|-----------------------|---------|--------------------|
 * | off                | (ignored)             | blocked | blocked            |
 * | on                 | off                   | allowed | blocked            |
 * | on                 | on                    | allowed | allowed            |
 *
 * Also drives the global <app-prepaid-card-modal> (mounted once in app.component.html).
 */
@Injectable({ providedIn: 'root' })
export class PrepaidCardService {
  readonly state = signal(false);
  readonly text = signal<string>(PREPAID_CARD_MESSAGES.notAccepted);

  private onUseDifferentCard: (() => void) | null = null;

  /** True when the Stripe card / saved source is a prepaid card. */
  isPrepaidCard(card: any): boolean {
    const funding =
      card?.funding ??
      card?.card?.funding ??
      card?.payment_method?.card?.funding ??
      card?.payment_method_details?.card?.funding ??
      card?.source?.card?.funding ??
      card?.source?.funding ??
      '';
    return String(funding).toLowerCase() === 'prepaid';
  }

  get settings(): { allowPrepaid: boolean; prepaidForRecurring: boolean; allowOneCard: boolean } {
    const doctorSettings = this.readDoctorSettings();
    return {
      allowPrepaid: this.isOn(doctorSettings?.allow_prepaid_card),
      prepaidForRecurring: this.isOn(doctorSettings?.prepaid_for_recurring),
      allowOneCard: this.isOn(doctorSettings?.allow_one_card)
    };
  }

  /**
   * Usage for a subscription's primary card, which pays today and every renewal.
   * With `allow_one_card` on it follows the payment rule, otherwise the recurring one.
   */
  get subscriptionCardUsage(): PrepaidCardUsage {
    return this.settings.allowOneCard ? 'payment' : 'recurring';
  }

  isBlocked(card: any, usage: PrepaidCardUsage): boolean {
    if (!this.isPrepaidCard(card)) {
      return false;
    }
    const { allowPrepaid, prepaidForRecurring } = this.settings;
    if (!allowPrepaid) {
      return true;
    }
    return usage === 'recurring' && !prepaidForRecurring;
  }

  /** Message matching why `card` is blocked for `usage`. */
  messageFor(usage: PrepaidCardUsage): string {
    return usage === 'recurring' && this.settings.allowPrepaid
      ? PREPAID_CARD_MESSAGES.notAcceptedForRecurring
      : PREPAID_CARD_MESSAGES.notAccepted;
  }

  /**
   * Call wherever a card is about to become the card in use (new or existing).
   * Returns `true` when the card may be used. When blocked, opens the modal and
   * returns `false`; "Use a Different Card" runs `onUseDifferentCard` (e.g. reopen
   * the card picker / focus the card form).
   */
  check(card: any, usage: PrepaidCardUsage, onUseDifferentCard?: () => void): boolean {
    if (!this.isBlocked(card, usage)) {
      return true;
    }
    this.open(this.messageFor(usage), onUseDifferentCard);
    return false;
  }

  open(text: string = PREPAID_CARD_MESSAGES.notAccepted, onUseDifferentCard?: () => void): void {
    this.text.set(text);
    this.onUseDifferentCard = onUseDifferentCard || null;
    this.state.set(true);
  }

  close(): void {
    this.state.set(false);
    this.onUseDifferentCard = null;
  }

  useDifferentCard(): void {
    const callback = this.onUseDifferentCard;
    this.close();
    callback?.();
  }

  /** Keeps the prepaid-related keys from access_token_login's doctor_settings. */
  static cacheSettings(doctorSettings: any): void {
    const settings = Array.isArray(doctorSettings) ? doctorSettings[0] : doctorSettings;
    localStorage.setItem(PREPAID_SETTINGS_KEY, JSON.stringify({
      allow_prepaid_card: settings?.allow_prepaid_card,
      prepaid_for_recurring: settings?.prepaid_for_recurring,
      allow_one_card: settings?.allow_one_card
    }));
  }

  private readDoctorSettings(): any {
    // doctorSettings can be overwritten by get_finance_values with another
    // payload, so keys cached at login take precedence.
    const doctorSettings = this.unwrap(this.readJson('doctorSettings'));
    const cached = this.readJson(PREPAID_SETTINGS_KEY) || {};
    const merged = { ...doctorSettings };
    Object.keys(cached).forEach((key) => {
      if (cached[key] !== undefined && cached[key] !== null) {
        merged[key] = cached[key];
      }
    });
    return merged;
  }

  private readJson(key: string): any {
    try {
      return JSON.parse(localStorage.getItem(key) || 'null');
    } catch {
      return null;
    }
  }

  private unwrap(settings: any): any {
    if (Array.isArray(settings)) {
      return settings[0] || {};
    }
    if (Array.isArray(settings?.data)) {
      return settings.data[0] || {};
    }
    return settings?.data || settings || {};
  }

  private isOn(value: any): boolean {
    return value === true || String(value).toLowerCase() === 'true' || Number(value) === 1;
  }
}
